/**
 * 语音录制 — MediaRecorder 包装，麦克风 → Blob。
 *
 * b 方案：手机只负责录音 + 把音频发出去，**不转写**。转写在桌面侧完成
 * (桌面用自己的额度调 /voice/transcribe)，所以这里没有任何 LLM 调用。
 *
 * 30min 硬上限自动停；录音不持久化，Blob 用完即弃。
 */

import { useCallback, useEffect, useRef, useState } from 'react'

// 30min 上限：录音直传 R2，不再受 base64-in-JSON 体积限制；30 分钟够覆盖
// 会议级录音。转写侧 deck 内联喂 Gemini 的 ~14MB 原始音频上限，对应 32kbps
// 约 58min，留足余量。
const MAX_MS = 30 * 60 * 1000
const REC_BITRATE = 32_000

/** 录音器错误码 — UI 层映射成 i18n 文案。 */
export type RecorderError = 'denied' | 'unavailable' | 'failed'

function pickMime(): string {
  const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus']
  for (const m of candidates) {
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(m)) return m
  }
  return ''
}

export interface RecorderState {
  recording: boolean
  elapsedMs: number
  /** 0..1 归一化音量，给录音脉冲动效用。 */
  level: number
  error: RecorderError | null
  start: () => Promise<void>
  /** 停止并返回 Blob；未在录则返回 null。 */
  stop: () => Promise<Blob | null>
  cancel: () => void
}

export function useRecorder(): RecorderState {
  const [recording, setRecording] = useState(false)
  const [elapsedMs, setElapsedMs] = useState(0)
  const [level, setLevel] = useState(0)
  const [error, setError] = useState<RecorderError | null>(null)

  const recorderRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<BlobPart[]>([])
  const streamRef = useRef<MediaStream | null>(null)
  const audioCtxRef = useRef<AudioContext | null>(null)
  const analyserRef = useRef<AnalyserNode | null>(null)
  const analyserStreamRef = useRef<MediaStream | null>(null)
  const rafRef = useRef<number | null>(null)
  const timerRef = useRef<number | null>(null)
  const startedAtRef = useRef(0)
  const stopResolverRef = useRef<((b: Blob | null) => void) | null>(null)
  const wakeLockRef = useRef<WakeLockSentinel | null>(null)

  const cleanup = useCallback(() => {
    if (timerRef.current !== null) clearInterval(timerRef.current)
    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current)
    timerRef.current = null
    rafRef.current = null
    streamRef.current?.getTracks().forEach((t) => t.stop())
    streamRef.current = null
    if (audioCtxRef.current && audioCtxRef.current.state !== 'closed') {
      audioCtxRef.current.close().catch(() => {})
    }
    audioCtxRef.current = null
    analyserRef.current = null
    analyserStreamRef.current?.getTracks().forEach((t) => t.stop())
    analyserStreamRef.current = null
    recorderRef.current = null
    wakeLockRef.current?.release().catch(() => {})
    wakeLockRef.current = null
  }, [])

  useEffect(() => () => cleanup(), [cleanup])

  const start = useCallback(async () => {
    if (recording) return
    setError(null)
    setElapsedMs(0)
    setLevel(0)
    chunksRef.current = []

    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      setError('unavailable')
      return
    }

    let stream: MediaStream
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    } catch (e) {
      const name = (e as Error)?.name
      setError(name === 'NotAllowedError' || name === 'SecurityError' ? 'denied' : 'unavailable')
      return
    }
    streamRef.current = stream

    const mimeType = pickMime()
    let recorder: MediaRecorder
    try {
      recorder = new MediaRecorder(stream, {
        ...(mimeType ? { mimeType } : {}),
        audioBitsPerSecond: REC_BITRATE,
      })
    } catch {
      setError('failed')
      cleanup()
      return
    }
    recorderRef.current = recorder

    recorder.ondataavailable = (ev) => {
      if (ev.data && ev.data.size > 0) chunksRef.current.push(ev.data)
    }
    recorder.onstop = () => {
      const type = recorder.mimeType || mimeType || 'audio/webm'
      const blob = chunksRef.current.length > 0 ? new Blob(chunksRef.current, { type }) : null
      const resolver = stopResolverRef.current
      stopResolverRef.current = null
      cleanup()
      setRecording(false)
      setLevel(0)
      resolver?.(blob)
    }
    recorder.onerror = () => setError('failed')

    // 音量分析 — AnalyserNode RMS，cosmetic，失败不影响录音
    try {
      const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
      const ctx = new Ctx()
      audioCtxRef.current = ctx
      // iOS WebKit:同一条 getUserMedia 流同时喂 MediaRecorder 和 AudioContext
      // (createMediaStreamSource)会让录音变静音 —— 波形照常动、录到的音频却是空的
      // (cortex-desktop#165 的招牌症状)。给波形分析用一条克隆 track,录音流不被
      // AudioContext 触碰。clone 失败(老浏览器)就退回原流,不影响录音主路径。
      let analyserSource: MediaStream = stream
      try {
        const t = stream.getAudioTracks()[0]
        if (t) {
          analyserSource = new MediaStream([t.clone()])
          analyserStreamRef.current = analyserSource
        }
      } catch { /* 退回原流 */ }
      const analyser = ctx.createAnalyser()
      analyser.fftSize = 256
      ctx.createMediaStreamSource(analyserSource).connect(analyser)
      analyserRef.current = analyser
      const buf = new Uint8Array(analyser.frequencyBinCount)
      const tick = () => {
        if (!analyserRef.current) return
        analyserRef.current.getByteFrequencyData(buf)
        let sum = 0
        for (let i = 0; i < buf.length; i++) sum += buf[i]
        setLevel(Math.min(1, sum / buf.length / 128))
        rafRef.current = requestAnimationFrame(tick)
      }
      rafRef.current = requestAnimationFrame(tick)
    } catch {
      // 无波形也能录
    }

    startedAtRef.current = performance.now()
    timerRef.current = window.setInterval(() => {
      const el = performance.now() - startedAtRef.current
      setElapsedMs(el)
      if (el >= MAX_MS && recorderRef.current?.state !== 'inactive') {
        recorderRef.current?.stop()
      }
    }, 100)

    recorder.start()
    setRecording(true)

    // 录音期间防锁屏 —— iOS 16.4+ / Chrome 支持；不支持则静默跳过（长录音可能
    // 被系统挂起，UI 侧提示用户保持页面打开）。页面切后台时浏览器会自动释放，
    // v1 不做重新获取。
    try {
      wakeLockRef.current = (await navigator.wakeLock?.request('screen')) ?? null
    } catch {
      wakeLockRef.current = null
    }
  }, [recording, cleanup])

  const stop = useCallback(async (): Promise<Blob | null> => {
    const recorder = recorderRef.current
    if (!recorder || recorder.state === 'inactive') return null
    return new Promise<Blob | null>((resolve) => {
      stopResolverRef.current = resolve
      recorder.stop()
    })
  }, [])

  const cancel = useCallback(() => {
    const recorder = recorderRef.current
    if (recorder && recorder.state !== 'inactive') {
      chunksRef.current = []
      recorder.stop()
    } else {
      cleanup()
      setRecording(false)
      setLevel(0)
    }
  }, [cleanup])

  return { recording, elapsedMs, level, error, start, stop, cancel }
}
